import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2, Quote, PenLine, Anchor, Heart, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { todayKey, prettyDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DailyCompass from "@/components/DailyCompass";

const shiftDate = (mmdd, delta) => {
  const [mm, dd] = mmdd.split("-").map((n) => parseInt(n, 10));
  const d = new Date(2024, mm - 1, dd);
  d.setDate(d.getDate() + delta);
  return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const Today = () => {
  const { refreshUser } = useAuth();
  const [dateKey, setDateKey] = useState(todayKey());
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fav, setFav] = useState(false);
  const [checkedIn, setCheckedIn] = useState(false);
  const isToday = dateKey === todayKey();

  const load = async (key) => {
    setLoading(true);
    try {
      const path = key === todayKey() ? "/entries/today" : `/entries/date/${key}`;
      const { data } = await api.get(path);
      setEntry(data);
      try {
        const f = await api.get(`/favorites/check/${key}`);
        setFav(!!(f.data?.is_favorite ?? f.data?.favorited ?? f.data));
      } catch (e) { setFav(false); }
    } catch (e) {
      toast.error("Could not load this reading");
      setEntry(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(dateKey); }, [dateKey]);

  const toggleFav = async () => {
    setFav((v) => !v);
    try { await api.post(`/favorites/${dateKey}`); }
    catch (e) { toast.error("Could not update favorite"); setFav((v) => !v); }
  };

  const checkin = async () => {
    try {
      await api.post("/streaks/checkin");
      setCheckedIn(true);
      toast.success("Checked in — your streak grows!");
      refreshUser();
    } catch (e) { toast.error("Could not check in"); }
  };

  return (
    <div className="px-4 sm:px-6 py-8">
      <div className="max-w-3xl mx-auto">
        <DailyCompass />
        <div className="flex items-center justify-between mb-8">
          <button data-testid="today-prev-day" onClick={() => setDateKey((k) => shiftDate(k, -1))}
            className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-secondary transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <p className="text-xs uppercase tracking-widest text-brass font-semibold">
              {isToday ? "Today's Reflection" : "Daily Reflection"}
            </p>
            <p className="font-heading text-xl font-semibold">{prettyDate(dateKey)}</p>
          </div>
          <button data-testid="today-next-day" onClick={() => setDateKey((k) => shiftDate(k, 1))}
            className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-secondary transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : !entry ? (
          <p className="text-center text-muted-foreground py-20">No reading available for this day.</p>
        ) : (
          <div className="animate-fade-up" data-testid="today-content">
            <div className="flex items-center justify-between gap-3 mb-5">
              {entry.month_theme && (
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/15 text-primary text-sm font-medium">
                  <Anchor className="w-3.5 h-3.5" /> {entry.month_theme}
                </div>
              )}
              <button data-testid="favorite-button" onClick={toggleFav}
                className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-full border transition-colors ${
                  fav ? "border-destructive text-destructive" : "border-border text-muted-foreground hover:text-foreground"
                }`}>
                <Heart className={`w-4 h-4 ${fav ? "fill-destructive" : ""}`} />
                {fav ? "Favorited" : "Favorite"}
              </button>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground leading-tight">{entry.title}</h1>
            {entry.quote && (
              <div className="mt-6 flex gap-3 text-foreground/80">
                <Quote className="w-6 h-6 text-brass shrink-0" />
                <p className="font-heading italic text-lg">
                  {entry.quote}
                  {entry.quote_author && <span className="block text-sm not-italic text-muted-foreground mt-1">— {entry.quote_author}</span>}
                </p>
              </div>
            )}
            <div className="mt-8 bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm">
              <p className="text-foreground/90 leading-loose text-lg whitespace-pre-wrap">{entry.reflection}</p>
            </div>
            {entry.affirmation && (
              <div className="mt-6 bg-primary text-primary-foreground rounded-2xl p-6 sm:p-8">
                <p className="text-xs uppercase tracking-widest opacity-70 font-semibold mb-2">Affirmation</p>
                <p className="font-heading text-xl sm:text-2xl italic leading-snug">{entry.affirmation}</p>
              </div>
            )}
            {Array.isArray(entry.integration_questions) && entry.integration_questions.length > 0 && (
              <div className="mt-8">
                <h3 className="font-heading text-xl font-semibold mb-4">Questions to sit with</h3>
                <ol className="space-y-3">
                  {entry.integration_questions.map((q, i) => (
                    <li key={i} className="flex gap-3 bg-card border border-border rounded-xl p-4">
                      <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-sm font-semibold flex items-center justify-center shrink-0">{i + 1}</span>
                      <span className="text-foreground/90 leading-relaxed">{q}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/journal" data-testid="today-to-journal"
                className="inline-flex items-center gap-2 bg-brass text-accent-foreground font-semibold px-6 py-3 rounded-full hover:opacity-90 active:scale-95 transition-all">
                <PenLine className="w-4 h-4" /> Reflect in your journal
              </Link>
              {isToday && (
                <button data-testid="checkin-button" onClick={checkin} disabled={checkedIn}
                  className="inline-flex items-center gap-2 border border-border font-semibold px-6 py-3 rounded-full hover:bg-secondary transition-colors disabled:opacity-60">
                  <CheckCircle2 className="w-4 h-4" /> {checkedIn ? "Checked in" : "Daily check-in"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Today;
